import { createApp, nextTick } from 'vue'
import App from '@/App.vue'
import router from '@/components/portal/messages/router'
import store from '@/store'
import { patchRouterNavigation } from '@/components/portal/common/utils/safeRouterNavigation'
import { installHiddenReplaceStateGuard } from '@/components/shared/utils/patchHistoryReplaceState'

patchRouterNavigation(router)
installHiddenReplaceStateGuard()

import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import '@/assets/css/tokens.css'
import '@/assets/css/main.css'
import '@/assets/css/hotspot-tabs.css'
import '@/assets/css/equipment-intro.css'
import '@/assets/css/image-gallery.css'
import '@/assets/css/table-tabs.css'
import 'katex/dist/katex.min.css'

import { bindPageviewTracking } from '@/utils/pageview'
import {
  beginHomeEntry,
  cancelHomeEntry,
  completeHomeEntry,
  dispatchHomeReset,
  scrollPageToTop
} from '@/components/home/utils/homeReset'
import scrollReveal from '@/components/shared/utils/scrollReveal'
import cspHtml from '@/components/shared/utils/cspHtml'
import { getRouteContentPath, isLocaleHomePath } from '@/components/portal/common/utils/localePath'
import {
  countRedirectChain,
  needsCanonicalRedirect,
  resolveLocaleForVisit
} from '@/components/portal/common/utils/localeNavigation'
import { setupDocumentTitleSync } from '@/components/portal/common/utils/pageTitle'
import {
  isPortalTabSwitch,
  scheduleScrollPortalTabContentTop,
  shouldPreserveScrollOnRouteChange,
  shouldResetPortalPageScroll,
  scrollPortalBelowHero
} from '@/components/portal/common/utils/portalScrollPreserve'
import { initViewportShell } from '@/components/shared/utils/viewportShell'

if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual'
}

function isRouteHome(route) {
  return Boolean(route?.meta?.isHome || route?.name === 'Home' || isLocaleHomePath(route?.path))
}

function scrollRoute(route) {
  return { ...route, path: getRouteContentPath(route) }
}

router.beforeEach(async (to, from) => {
  await store.dispatch('locale/fetchLocales').catch(() => {})

  const enabled = store.state.locale.locales || []
  const isInitial = !from.matched.length
  const resolved = resolveLocaleForVisit({
    path: to.path,
    query: to.query,
    enabled,
    sessionLocale: store.state.locale.current,
    currentLocale: store.state.locale.current,
    isInitial
  })

  if (resolved.locale && resolved.locale !== store.state.locale.current) {
    await store.dispatch('locale/setLocale', resolved.locale).catch(() => {})
  }

  const locale = store.state.locale.current || resolved.locale
  if (countRedirectChain(to) <= 2) {
    const redirect = needsCanonicalRedirect(to, locale)
    if (redirect) return redirect
  }

  if (isRouteHome(to) && from.matched.length && !isRouteHome(from)) {
    beginHomeEntry()
    return
  }
  if (isRouteHome(from) && !isRouteHome(to)) {
    cancelHomeEntry()
  }
})

router.afterEach((to, from) => {
  if (from.matched.length && getRouteContentPath(to) === getRouteContentPath(from)) {
    return
  }

  const enteredHomeFromOtherPage =
    isRouteHome(to) && from.matched.length && !isRouteHome(from)

  nextTick(() => {
    if (isRouteHome(to)) {
      if (enteredHomeFromOtherPage) {
        completeHomeEntry()
      } else {
        dispatchHomeReset()
      }
      return
    }

    const menus = store.state.nav?.rawMenus || []
    const locale = store.state.locale?.current
    const toContent = scrollRoute(to)
    const fromContent = scrollRoute(from)
    if (isPortalTabSwitch(toContent, fromContent, menus, locale)) {
      scheduleScrollPortalTabContentTop()
      return
    }
    if (shouldPreserveScrollOnRouteChange(toContent, fromContent, menus)) {
      return
    }
    if (shouldResetPortalPageScroll(toContent, fromContent, menus, locale)) {
      scrollPortalBelowHero()
      return
    }
    scrollPageToTop()
  })
})

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return
  if (isRouteHome(router.currentRoute.value)) {
    beginHomeEntry()
    nextTick(() => {
      completeHomeEntry()
    })
    return
  }
  store.dispatch('nav/fetchMenus').catch(() => {})
})

const app = createApp(App)

app.use(router)
bindPageviewTracking(router)
app.use(store)

setupDocumentTitleSync(router, store)

app.use(ElementPlus)
app.directive('scroll-reveal', scrollReveal)
app.directive('csp-html', cspHtml)

store.dispatch('locale/fetchLocales').catch(() => {})

app.mount('#app')

initViewportShell()
